/* eslint-disable operator-linebreak */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { FC, useCallback, useState, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Button, { Themes } from 'ui/Button/Button';
import api from 'api/api';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import { AxiosStatus } from 'api/axiosConfig';
import Loader from 'ui/Loader/Loader';
import { IApiResponse } from 'utils/models';
import { IModalProps } from '../IModalProps';
import ModalWindow from '../ModalWindow/ModalWindow';
import StatusModal from '../StatusModal/StatusModal';
import { IDownloadStatementResponse } from './DownloadStatementModal.model';
import styles from './DownloadStatementModal.module.css';

interface IDownloadStatementModalProps extends IModalProps {
  activeAccountNumber: string;
  startDate: string;
  endDate: string;
}

const DownloadStatementModal: FC<IDownloadStatementModalProps> = ({
  open,
  onClose,
  activeAccountNumber,
  startDate,
  endDate,
}) => {
  const [fetching, setFetching] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);

  const closeStatusModal = useCallback(() => {
    setSubmitStatus(AxiosStatus.DEFAULT);
    onClose();
  }, [onClose]);

  const showMainModal = useCallback(() => {
    setSubmitStatus(AxiosStatus.DEFAULT);
  }, []);

  const doDownloadStatement = useCallback(
    (type: 'PDF' | 'Excel') => {
      const body = {
        startDate,
        endDate,
        accountNumber: activeAccountNumber,
        isDownload: true,
        downloadType: type,
      };
      setFetching(true);
      api
        .post('account/transactions/statement', body)
        .then((data: IDownloadStatementResponse) => {
          const ext = type === 'Excel' ? 'xls' : 'pdf';
          const obscureAccount = `${activeAccountNumber.substring(0, activeAccountNumber.length - 5)}xxxxx`;

          const url =
            type === 'Excel'
              ? `data:application/vnd.ms-excel;base64,${data.file}`
              : `data:application/pdf;base64,${data.file}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = `Account Statement ${obscureAccount} ${startDate}_${endDate}.${ext}`;
          downloadLink.click();
          setSuccessMsg('Statement Download started');
          setSubmitStatus(AxiosStatus.SUCCESS);
        })
        .catch((error: IApiResponse) => {
          setErrorMsg(error.description || 'Could not download statement, please try again.');
          setSubmitStatus(AxiosStatus.FAILURE);
        })
        .finally(() => setFetching(false));
    },
    [activeAccountNumber, endDate, startDate],
  );

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const showStatusModal = useMemo(
    () => submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE,
    [submitStatus],
  );

  const statusModalState = useMemo(
    () => (submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'),
    [submitStatus],
  );

  return (
    <>
      <ModalWindow open={open && !showStatusModal} onClose={onClose} fullScreen>
        <>
          <NavigationTitle to="/" layoutType={LayoutType.dashboard} onClick={onClose} noArrow={!downMdScreen}>
            Download Statement
          </NavigationTitle>

          <div className={styles.message}>Select statement format you want to download.</div>

          <Grid container justifyContent="center" spacing={2}>
            {fetching && (
              <Grid item xs={12}>
                <Loader />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={6}>
              <Button onClick={() => doDownloadStatement('PDF')} theme={Themes.secondary} disabled={fetching} fullWidth>
                PDF
              </Button>
            </Grid>
            {/* commented initially */}
            <Grid item xs={12} sm={6} md={6}>
              <Button
                onClick={() => doDownloadStatement('Excel')}
                theme={Themes.secondary}
                disabled={fetching}
                fullWidth
              >
                Excel
              </Button>
            </Grid>
          </Grid>
        </>
      </ModalWindow>

      <StatusModal
        open={open && showStatusModal}
        status={statusModalState}
        onClose={closeStatusModal}
        onRetry={showMainModal}
      >
        {submitStatus === AxiosStatus.FAILURE ? errorMsg : successMsg}
      </StatusModal>
    </>
  );
};

export default DownloadStatementModal;
